import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPlus,
  faTrophy,
  faUsers,
  faLayerGroup,
  faCrown,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLeagueId } from "../../features/league/leagueReducer";

const LeaguesCard = ({ leagues = [], onLeagueClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (leagues.length <= 1) return;

    const interval = setInterval(() => {
      if (!isAnimating) {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % leagues.length);
      }
    }, 8000);

    return () => clearInterval(interval);
  }, [leagues.length, isAnimating]);

  const handleNext = () => {
    if (currentIndex < leagues.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex(currentIndex + 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex(currentIndex - 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      handleNext();
    } else if (touchEndX.current - touchStartX.current > 50) {
      handlePrev();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "TBD";

    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "TBD";
    }
  };

  const getStatusColor = (status) => {
    if (!status) return "bg-gray-400";

    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "inactive":
        return "bg-red-500";
      case "archived":
        return "bg-gray-500";
      default:
        return "bg-gray-400";
    }
  };

  const isNearingEnd = (endDate) => {
    if (!endDate) return false;

    const end = new Date(endDate);
    const now = new Date();
    const daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

    return daysRemaining <= 7 && daysRemaining > 0;
  };

  const handleLeagueClick = (leagueId) => {
    dispatch(setLeagueId(leagueId));
    navigate("/leagues");
  };

  if (!leagues || leagues.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-dark-grey to-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faLayerGroup}
                className="text-deep-red w-5 h-5"
              />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Your Leagues</h2>
              <p className="text-white/80 text-sm">
                Private leagues you've joined
              </p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className="p-4 text-center flex flex-col justify-center items-center min-h-[200px]">
          <div className="bg-gray-100 rounded-full p-4 mb-4 w-16 h-16 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faTrophy}
              className="text-deep-red w-6 h-6"
            />
          </div>
          <h3 className="text-dark-grey font-bold text-lg mb-2">
            No Leagues Yet
          </h3>
          <p className="text-medium-grey text-sm max-w-[250px] mb-4">
            Create your first league or join existing ones to start competing.
          </p>
          <button
            onClick={() => navigate("/create-league")}
            className="bg-deep-red text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-bright-red transition-colors min-h-[44px] flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
            Create League
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-dark-grey to-gray-700 p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faLayerGroup}
              className="text-deep-red w-5 h-5"
            />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Your Leagues</h2>
            <p className="text-white/80 text-sm">
              {`${currentIndex + 1} of ${leagues.length} ${
                leagues.length === 1 ? "league" : "leagues"
              }`}
            </p>
          </div>
        </div>

        {/* Pagination dots */}
        {leagues.length > 1 && (
          <div className="flex gap-1">
            {leagues.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-gold"
                    : "bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className="p-4 min-h-[280px] flex flex-col"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex-1 overflow-hidden">
          <div
            className="flex transition-transform duration-300 ease-in-out h-full"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {leagues.map((league) => (
              <div
                key={league._id || league.id}
                className="min-w-full flex-shrink-0 pr-0"
              >
                <div
                  onClick={() => handleLeagueClick(league._id || league.id)}
                  className="bg-gray-50 border border-gray-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all duration-200 h-full flex flex-col"
                >
                  {/* League Header */}
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-dark-grey flex-1 pr-2 line-clamp-2">
                      {league.leagueName || league.name}
                    </h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <div
                        className={`w-2 h-2 rounded-full ${getStatusColor(
                          league.status,
                        )}`}
                      ></div>
                      <span className="text-xs font-medium text-medium-grey capitalize">
                        {league.status}
                      </span>
                    </div>
                  </div>

                  {/* Warning Banner */}
                  {isNearingEnd(league.endDate) && (
                    <div className="mb-3 flex items-center gap-2 p-2 rounded-lg bg-gold/10 border border-gold/20">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="text-gold w-3 h-3"
                      />
                      <p className="text-xs text-dark-grey font-medium">
                        Ends {formatDate(league.endDate)}
                      </p>
                    </div>
                  )}

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 flex-1">
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-deep-red/10 flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-deep-red w-3 h-3"
                          />
                        </div>
                        {league.openSeats > 0 && (
                          <span className="text-xs bg-deep-red text-white px-1.5 py-0.5 rounded-full font-bold">
                            {league.openSeats} open
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-medium-grey mb-1">Members</p>
                      <p className="text-lg font-bold text-dark-grey">
                        {league.memberCount || 0}/{league.totalSeats || 0}
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={league.userRank === 1 ? faCrown : faTrophy}
                            className={`w-3 h-3 ${
                              league.userRank === 1
                                ? "text-gold"
                                : "text-deep-red"
                            }`}
                          />
                        </div>
                        {league.userRank === 1 && (
                          <span className="text-xs bg-gold text-dark-grey px-1.5 py-0.5 rounded-full font-bold">
                            1st
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-medium-grey mb-1">Your Rank</p>
                      <p
                        className={`text-lg font-bold ${
                          league.userRank === 1 ? "text-gold" : "text-dark-grey"
                        }`}
                      >
                        {league.userRank ? `#${league.userRank}` : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Controls */}
        {leagues.length > 1 && (
          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0 || isAnimating}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                currentIndex === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-dark-grey text-white hover:bg-deep-red"
              }`}
              aria-label="Previous League"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
            </button>
            <button
              onClick={handleNext}
              disabled={currentIndex === leagues.length - 1 || isAnimating}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                currentIndex === leagues.length - 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-dark-grey text-white hover:bg-deep-red"
              }`}
              aria-label="Next League"
            >
              <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4">
        <div className="text-center">
          <p className="text-sm text-medium-grey mb-3">
            Want to create your own league?
          </p>
          <button
            onClick={() => navigate("/create-league")}
            className="w-full bg-deep-red text-white py-3 rounded-xl text-sm font-bold hover:bg-bright-red transition-colors min-h-[44px] flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
            Create New League
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaguesCard;
